// =============================================================================
// FORM BUILDER - Professional form builder like SurveyJS
// =============================================================================

import { useState, useCallback } from 'react';
import { Eye, EyeOff, Save, Settings, Undo, Redo, Download, Upload, Code2, LayoutTemplate } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { FieldPalette } from './components/FieldPalette';
import { FormCanvas } from './components/FormCanvas';
import { FieldConfigPanel } from './components/FieldConfigPanel';
import { FormSettingsPanel } from './components/FormSettingsPanel';
import { FormTemplatesPanel } from './components/FormTemplatesPanel';
import { ModuleAIPrompt } from '../../components/ModuleAIPrompt';
import { useFormBuilderStore } from './store';
import type { FieldType, Form } from './types';

interface FormBuilderProps {
  onSave?: (form: Form) => void;
}

export function FormBuilder({ onSave }: FormBuilderProps) {
  const [showSettings, setShowSettings] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [isAIProcessing, setIsAIProcessing] = useState(false);
  const {
    sections,
    selectedFieldId,
    previewMode,
    theme,
    addField,
    togglePreview,
    exportForm,
    importForm,
    form,
  } = useFormBuilderStore();

  // AI prompt execution handler for form builder
  const handleAIExecute = useCallback(async (prompt: string) => {
    setIsAIProcessing(true);
    try {
      // Parse the prompt and execute form-related commands
      const lowerPrompt = prompt.toLowerCase();
      const sectionId = sections[0]?.id;

      if (!sectionId) return;

      // Simple pattern matching for common form requests
      // In a real implementation, this would call an AI service

      // Pattern: "créer/ajouter X champs" or "create X fields"
      const fieldCountMatch = lowerPrompt.match(/(\d+)\s*champs?|(\d+)\s*fields?/);
      if (fieldCountMatch) {
        const countStr = fieldCountMatch[1] ?? fieldCountMatch[2];
        if (countStr) {
          const count = parseInt(countStr, 10);
          const fieldTypes: FieldType[] = ['text', 'email', 'number', 'textarea', 'select', 'date'];
          for (let i = 0; i < Math.min(count, 10); i++) {
            const fieldType = fieldTypes[i % fieldTypes.length];
            if (fieldType) {
              addField(sectionId, fieldType);
            }
          }
          return;
        }
      }

      // Pattern: add specific field type
      if (lowerPrompt.includes('email') || lowerPrompt.includes('courriel')) {
        addField(sectionId, 'email');
      } else if (lowerPrompt.includes('téléphone') || lowerPrompt.includes('phone')) {
        addField(sectionId, 'phone');
      } else if (lowerPrompt.includes('date')) {
        addField(sectionId, 'date');
      } else if (lowerPrompt.includes('texte long') || lowerPrompt.includes('textarea') || lowerPrompt.includes('message')) {
        addField(sectionId, 'textarea');
      } else if (lowerPrompt.includes('choix') || lowerPrompt.includes('select') || lowerPrompt.includes('liste')) {
        addField(sectionId, 'select');
      } else if (lowerPrompt.includes('case à cocher') || lowerPrompt.includes('checkbox')) {
        addField(sectionId, 'checkbox');
      } else if (lowerPrompt.includes('radio') || lowerPrompt.includes('choix unique')) {
        addField(sectionId, 'radio');
      } else if (lowerPrompt.includes('fichier') || lowerPrompt.includes('file') || lowerPrompt.includes('upload')) {
        addField(sectionId, 'file');
      } else if (lowerPrompt.includes('nombre') || lowerPrompt.includes('number') || lowerPrompt.includes('numérique')) {
        addField(sectionId, 'number');
      } else {
        // Default: add a text field
        addField(sectionId, 'text');
      }

      // Simulate AI processing delay
      await new Promise(resolve => setTimeout(resolve, 500));
    } finally {
      setIsAIProcessing(false);
    }
  }, [sections, addField]);

  const handleDragStart = useCallback(
    (event: React.DragEvent, fieldType: FieldType) => {
      event.dataTransfer.setData('application/formfield', fieldType);
      event.dataTransfer.effectAllowed = 'move';
    },
    []
  );

  const handleAddField = useCallback(
    (fieldType: FieldType) => {
      const sectionId = sections[0]?.id;
      if (sectionId) {
        addField(sectionId, fieldType);
      }
    },
    [sections, addField]
  );

  const handleSave = useCallback(() => {
    const formData = exportForm();
    if (onSave) {
      onSave(formData);
    }
  }, [exportForm, onSave]);

  const handleExportJson = useCallback(() => {
    const formData = exportForm();
    const json = JSON.stringify(formData, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formData.name.replace(/\s+/g, '_').toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [exportForm]);

  const handleImportJson = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const formData = JSON.parse(event.target?.result as string) as Form;
            importForm(formData);
          } catch (error) {
            console.error('Error parsing JSON:', error);
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  }, [importForm]);

  // Count total fields
  const totalFields = sections.reduce((acc, s) => acc + s.fields.length, 0);

  // Determine which right panel to show
  const showFieldConfig = !previewMode && selectedFieldId && !showSettings;
  const showFormSettings = !previewMode && showSettings;

  return (
    <div className="flex flex-col h-full bg-secondary-50 dark:bg-secondary-950">
      {/* Professional Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-secondary-200 bg-white dark:border-secondary-700 dark:bg-secondary-900 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: theme.primaryColor }}
            />
            <h2 className="font-semibold text-secondary-900 dark:text-white">
              {form?.name || 'Nouveau formulaire'}
            </h2>
          </div>

          <div className="flex items-center gap-2 text-xs">
            <Badge variant="secondary" className="font-normal">
              {sections.length} section{sections.length > 1 ? 's' : ''}
            </Badge>
            <Badge variant="secondary" className="font-normal">
              {totalFields} champ{totalFields > 1 ? 's' : ''}
            </Badge>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Undo/Redo (placeholder for future implementation) */}
          <div className="flex items-center gap-1 mr-2">
            <Button variant="ghost" size="icon" className="h-8 w-8" disabled>
              <Undo className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" disabled>
              <Redo className="h-4 w-4" />
            </Button>
          </div>

          {/* Templates button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowTemplates(true)}
          >
            <LayoutTemplate className="h-4 w-4 mr-2" />
            Modèles
          </Button>

          {/* Preview toggle */}
          <Button
            variant={previewMode ? 'default' : 'outline'}
            size="sm"
            onClick={togglePreview}
          >
            {previewMode ? (
              <>
                <EyeOff className="h-4 w-4 mr-2" />
                Éditer
              </>
            ) : (
              <>
                <Eye className="h-4 w-4 mr-2" />
                Aperçu
              </>
            )}
          </Button>

          {/* Settings */}
          <Button
            variant={showSettings ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowSettings(!showSettings)}
          >
            <Settings className="h-4 w-4 mr-2" />
            Paramètres
          </Button>

          {/* Import/Export menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Code2 className="h-4 w-4 mr-2" />
                Import/Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleImportJson}>
                <Upload className="h-4 w-4 mr-2" />
                Importer JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportJson}>
                <Download className="h-4 w-4 mr-2" />
                Exporter JSON
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <Code2 className="h-4 w-4 mr-2" />
                Voir le code JSON
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Save button */}
          <Button size="sm" onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            Enregistrer
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left sidebar - Field palette with AI prompt */}
        {!previewMode && (
          <div className="w-80 border-r border-secondary-200 dark:border-secondary-700 flex-shrink-0 overflow-hidden flex flex-col">
            {/* AI Prompt at top */}
            <div className="p-3 border-b border-secondary-200 dark:border-secondary-700">
              <ModuleAIPrompt
                moduleType="form"
                onExecute={handleAIExecute}
                isProcessing={isAIProcessing}
                compact
              />
            </div>
            {/* Field palette below */}
            <div className="flex-1 overflow-hidden">
              <FieldPalette
                onDragStart={handleDragStart}
                onAddField={handleAddField}
              />
            </div>
          </div>
        )}

        {/* Main canvas */}
        <div className="flex-1 overflow-hidden">
          <FormCanvas />
        </div>

        {/* Right sidebar - Field config or Form settings */}
        {showFieldConfig && (
          <div className="w-80 border-l border-secondary-200 dark:border-secondary-700 flex-shrink-0">
            <FieldConfigPanel />
          </div>
        )}

        {showFormSettings && (
          <div className="w-80 border-l border-secondary-200 dark:border-secondary-700 flex-shrink-0">
            <FormSettingsPanel onClose={() => setShowSettings(false)} />
          </div>
        )}
      </div>

      {/* Templates Panel Modal */}
      {showTemplates && (
        <FormTemplatesPanel onClose={() => setShowTemplates(false)} />
      )}
    </div>
  );
}
